import type { TFunction } from 'i18next';
import { resolveI18nText, type Locale } from '@blauturquesa/shared/locales';
import type { PublicCaption, PublicPhoto, PublicPriceFrom, PublicSpecItem } from '@blauturquesa/shared/public-view';
import type { PropertyMode } from '@blauturquesa/shared/public-view';
import { photoSrc, photoSrcSet } from '../media/photoUrl';

type Text = Partial<Record<Locale, string>>;

/** Text en l'idioma del visitant o, si no hi és, en el d'origen (§11.2). */
export const pick = (value: Text | undefined, locale: Locale, source: Locale) => resolveI18nText(value, locale, source);

/** Foto amb totes les mides (`srcset`): el navegador tria la que toca (principi 3). */
export function Photo({ photo, sizes, locale, source, eager, className }: {
  photo: PublicPhoto;
  sizes: string;
  locale: Locale;
  source: Locale;
  eager?: boolean;
  className?: string;
}) {
  return (
    <img
      className={className}
      src={photoSrc(photo.variants, 1280)}
      srcSet={photoSrcSet(photo.variants)}
      sizes={sizes}
      width={photo.width}
      height={photo.height}
      alt={pick(photo.alt, locale, source).text}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      {...(eager ? { fetchPriority: 'high' as const } : {})}
    />
  );
}

/** Valor d'una característica de la fitxa, llegible (§5). */
export function formatSpec(item: PublicSpecItem, locale: Locale, source: Locale, t: TFunction): string {
  const num = (n: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n);
  const list = (xs: string[]) => new Intl.ListFormat(locale, { type: 'conjunction' }).format(xs);
  const opt = (k: string) => pick(item.options?.[k], locale, source).text || k;
  const v = item.value as unknown;
  switch (item.type) {
    case 'boolean':
      return v ? t('specs.yes') : t('specs.no');
    case 'number':
      // Un any no porta separador de milers («1890», no «1.890»).
      if (item.unit === 'year') return String(v);
      return item.unit === 'm2' || item.unit === 'mbps' ? `${num(Number(v))} ${t(`specs.units.${item.unit}`)}` : num(Number(v));
    case 'select':
      return opt(String(v));
    case 'multiselect':
      return list((Array.isArray(v) ? v : []).map((k) => opt(String(k))));
    case 'string':
      return String(v);
    case 'text':
      return pick(v as Text, locale, source).text;
    case 'distance': {
      const d = v as { km?: number; minutes?: number };
      return [d.km !== undefined ? `${num(d.km)} km` : null, d.minutes !== undefined ? `${num(d.minutes)} min` : null].filter(Boolean).join(' · ');
    }
    case 'bedrooms': {
      const rooms = (Array.isArray(v) ? v : []) as { beds: { type: string; count: number }[] }[];
      return rooms
        .map((r, i) => `${t('specs.bedroom', { n: i + 1 })}: ${list(r.beds.map((b) => t(`specs.beds.${b.type}`, { count: b.count })))}`)
        .join(' · ');
    }
    default:
      return '';
  }
}

/**
 * Text del preu «des de» (§10.2), segons el tipus d'operació: lloguer curt per nit, llarg per mes.
 * Sense preu per a cap dels tipus de la casa, no en surt cap (mai un «des de 0 €»).
 */
export function priceFromText(price: PublicPriceFrom | undefined, modes: readonly PropertyMode[], locale: Locale, t: TFunction): string | null {
  if (!price) return null;
  const money = (c: number) => new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(c / 100);
  if (modes.includes('shortRent') && price.nightCents !== undefined) return t('public:captions.fromNight', { price: money(price.nightCents) });
  if (modes.includes('longRent') && price.monthCents !== undefined) return t('public:captions.fromMonth', { price: money(price.monthCents) });
  return null;
}

/** Text d'un peu destacat; null si no s'ha de mostrar. */
export function captionText(c: PublicCaption | undefined, ctx: { price?: PublicPriceFrom; modes: readonly PropertyMode[]; locale: Locale; source: Locale; t: TFunction }): string | null {
  if (!c) return null;
  if (c.kind === 'text') return pick(c.text, ctx.locale, ctx.source).text || null;
  if (c.kind === 'priceFrom') return priceFromText(ctx.price, ctx.modes, ctx.locale, ctx.t);
  const num = (n: number) => new Intl.NumberFormat(ctx.locale, { maximumFractionDigits: 1 }).format(n);
  const parts = [c.km !== undefined ? `${num(c.km)} km` : null, c.minutes !== undefined ? `${c.minutes} min` : null].filter(Boolean);
  return parts.length > 0 ? `${pick(c.label, ctx.locale, ctx.source).text} · ${parts.join(' · ')}` : null;
}

/** Peu destacat damunt una foto: el punt brillant de §10 (degradat de la skin, petit). */
export function CaptionTag({ text, className }: { text: string | null; className?: string }) {
  return text ? <span className={`caption-tag${className ? ` ${className}` : ''}`}>{text}</span> : null;
}
